import { ApiEndpoint } from '@/api/registry';
import { useApiQuery } from '@/api/useApiQuery';
import type { ScenarioDetail } from '@/features/decisions/scenarios.api.types';
import { useTenant } from '@/providers/TenantProvider';

// Matches the service's own status refresh window (STATUS_REFRESH_SECONDS = 10).
const RUNNING_POLL_MS = 10_000;

const TERMINAL_STATUSES: ReadonlySet<string> = new Set(['succeeded', 'failed']);

// One scenario with all its runs and each run's candidate labels.
//
// The labels are names only. ClickHouse is the authority on which experiments actually
// produced results, because a run can load fewer than its config declared.
export function useScenario(scenarioRunId: string) {
  const { tenantAlias } = useTenant();

  return useApiQuery<ScenarioDetail>(
    tenantAlias,
    ApiEndpoint.ScenarioGet,
    { pathParams: { scenarioRunId } },
    {
      enabled: Boolean(scenarioRunId),
      retry: false,
      staleTime: 30_000,
      // Poll only while a run can still change. `!= null` covers a service that predates
      // `status`: undefined would read as live and poll forever on a page that cannot
      // update.
      refetchInterval: (query) => {
        const runs = query.state.data?.executions ?? [];
        const anyLive = runs.some((r) => r.status != null && !TERMINAL_STATUSES.has(r.status));
        return anyLive ? RUNNING_POLL_MS : false;
      },
    },
  );
}
